import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuth } from '@/modules/auth/AuthContext';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { apiErrorMessage } from '@/shared/api-client/workspace';
import { Button } from '@/shared/ui/components/button';
import { OperatingHoursPicker } from './OperatingHoursPicker';
import { useUpdateVenueOperatingHours, useVenueOperatingHours } from './operatingHours';
import {
  toEditableWeekdayHours,
  toRequestWeekdayHours,
  type EditableWeekdayHours,
  validateEditableWeekdayHours,
} from './operatingHoursForm';

export function OperatingHoursPage() {
  const { t } = useTranslation('common');
  const { venues } = useAuth();
  const { venueId } = useVenueContext();
  const venue = venues.find((candidate) => candidate.id === venueId);
  const heading = (
    <h1 className="text-2xl font-semibold">{t('venues.operatingHours.pageTitle')}</h1>
  );

  if (venue?.slotGranularityMinutes === undefined) {
    return <section className="grid gap-6 p-6">{heading}</section>;
  }

  return (
    <section className="grid gap-6 p-6">
      <div>
        {heading}
        <p className="mt-1 text-sm text-muted-foreground">
          {t('venues.operatingHours.scopedDescription', {
            name: venue.name,
            minutes: venue.slotGranularityMinutes,
          })}
        </p>
      </div>
      <OperatingHoursEditor
        venueId={venueId}
        slotGranularityMinutes={venue.slotGranularityMinutes}
      />
    </section>
  );
}

/**
 * The hours form of one Venue. It renders only once the Venue's SlotGranularity is known, because
 * the picker and validator offer and accept only that grid's times (ADR 0081).
 */
function OperatingHoursEditor({
  venueId,
  slotGranularityMinutes,
}: {
  venueId: string;
  slotGranularityMinutes: number;
}) {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const canManage = auth.can('venue.operating_hours.manage', { kind: 'venue', venueId });

  const { data, isLoading, isError } = useVenueOperatingHours(venueId);
  const update = useUpdateVenueOperatingHours(venueId);

  const [weekdayHours, setWeekdayHours] = useState<EditableWeekdayHours[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setWeekdayHours(data.weekdayHours.map(toEditableWeekdayHours));
  }, [data]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    const validationError = validateEditableWeekdayHours(weekdayHours, slotGranularityMinutes);
    if (validationError) {
      setError(t(`venues.operatingHours.errors.${validationError}`));
      return;
    }

    try {
      await update.mutateAsync({
        weekdayHours: weekdayHours.map(toRequestWeekdayHours),
      });
      toast.success(t('venues.operatingHours.toast.updated'));
    } catch (caught) {
      setError(apiErrorMessage(t, 'venues.operatingHours', caught));
    }
  }

  return (
    <>
      {isLoading && <p className="text-muted-foreground">{t('venues.operatingHours.loading')}</p>}
      {isError && (
        <p className="text-destructive">{t('venues.operatingHours.errors.loadFailed')}</p>
      )}

      {weekdayHours.length > 0 && (
        <form onSubmit={submit} className="grid max-w-3xl gap-4">
          <OperatingHoursPicker
            slotGranularityMinutes={slotGranularityMinutes}
            hours={weekdayHours}
            onHours={setWeekdayHours}
            disabled={!canManage}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          {canManage && (
            <div>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending
                  ? t('venues.operatingHours.form.submitting')
                  : t('venues.operatingHours.form.save')}
              </Button>
            </div>
          )}
        </form>
      )}
    </>
  );
}
